import { computed, watch, onMounted, ref } from 'vue';

function useLayoutComposable({ clinicianStreams, streamsLayoutStore }) {
  const layoutWrapperRef = ref();

  const clinicianStreamCount = computed(() => clinicianStreams.value.length);
  const isStreamFocused = computed(() => streamsLayoutStore.focusedStreamId);
  const multipleNotFocusedStreams = computed(
    () =>
      !isStreamFocused.value &&
      (clinicianStreamCount.value == 5 || clinicianStreamCount.value == 6),
  );

  const aspectRatio = computed(() => {
    if (multipleNotFocusedStreams.value) return ((9 / 16) * 2) / 3;
    if (!isStreamFocused.value && clinicianStreamCount.value == 2) return (9 / 16) * 2;
    return 9 / 16;
  });

  const aspectRatioCss = computed(() => {
    if (!isStreamFocused.value && clinicianStreamCount.value == 2)
      return {
        paddingTop: `${layoutWrapperRef.value.offsetHeight}px`,
        width: `${(layoutWrapperRef.value.offsetHeight * 16) / 9 / 2}px`,
      };

    if (multipleNotFocusedStreams.value)
      return {
        paddingTop: `${(((9 / 16) * 2) / 3) * 100}%`,
        width: '100%',
      };

    return {
      paddingTop: `${(9 / 16) * 100}%`,
      width: '100%',
    };
  });
  const relayoutGrid = () => {
    // Return if the layoutwrapper has not yet been rendered
    if (!layoutWrapperRef.value) return;
    // If two streams
    if (!isStreamFocused.value && clinicianStreamCount.value == 2) {
      layoutWrapperRef.value.style.padding = '0';
    } else if (
      // If its landscape
      layoutWrapperRef.value.offsetWidth * aspectRatio.value >
      layoutWrapperRef.value.offsetHeight
    ) {
      layoutWrapperRef.value.style.padding = `0 ${
        (layoutWrapperRef.value.offsetWidth -
          (layoutWrapperRef.value.offsetHeight * 1) / aspectRatio.value) /
        2
      }px `;
    } else {
      layoutWrapperRef.value.style.padding = '0';
    }
  };
  const gridLayoutClasses = computed(() => ({
    'grid-cols-1 grid-rows-1':
      streamsLayoutStore.focusedStreamId || clinicianStreams.value.length < 2,
    'grid-cols-1 grid-rows-2':
      !streamsLayoutStore.focusedStreamId && clinicianStreams.value.length == 2,
    'grid-cols-2 grid-rows-2':
      !streamsLayoutStore.focusedStreamId &&
      (clinicianStreams.value.length == 3 || clinicianStreams.value.length == 4),
    'grid-cols-3 grid-rows-2':
      !streamsLayoutStore.focusedStreamId && clinicianStreams.value.length > 4,
    'grid-cols-3 grid-rows-3':
      !streamsLayoutStore.focusedStreamId && clinicianStreams.value.length > 6,
  }));
  const shouldShowFocusToggle = (clinicianStreamId) =>
    (!streamsLayoutStore.focusedStreamId ||
      streamsLayoutStore.focusedStreamId == clinicianStreamId) &&
    clinicianStreams.value.length > 1;

  onMounted(() => {
    relayoutGrid();
    new ResizeObserver(relayoutGrid).observe(layoutWrapperRef.value);
    new ResizeObserver(relayoutGrid).observe(document.getElementsByTagName('body')[0]);
  });

  watch(clinicianStreams, (newVal) => {
    const streamIds = newVal.map((streamData) => streamData.jitsiUserId);
    if (!streamIds.includes(streamsLayoutStore.focusedStreamId)) {
      streamsLayoutStore.disableFocusMode();
    }
    relayoutGrid();
  });

  const toggleFocus = (streamId) => {
    streamsLayoutStore.focusStream(streamId);
    relayoutGrid();
  };

  const getGridItemClasses = (clinicianStream) => ({
    'w-full mx-auto': !streamsLayoutStore.focusedStreamId && clinicianStreams.length == 2,
    'flex flex-col absolute left-0 w-40 z-10 border rounded-2xl':
      shouldBeMinimized(clinicianStream),
  });

  const getGridItemStyle = (clinicianStream, index) => {
    if (streamsLayoutStore.focusedStreamId) {
      const focusedStreamIndex = _.findIndex(
        clinicianStreams.value,
        (stream) => stream.jitsiUserId == streamsLayoutStore.focusedStreamId,
      );

      return {
        top: index < focusedStreamIndex ? `${index * 90}px` : `${(index - 1) * 90}px`,
      };
    }
    return {};
  };

  const shouldBeMinimized = (clinicianStream) =>
    streamsLayoutStore.focusedStreamId &&
    streamsLayoutStore.focusedStreamId != clinicianStream.jitsiUserId;

  return {
    aspectRatioCss,
    gridLayoutClasses,
    shouldShowFocusToggle,
    layoutWrapperRef,
    toggleFocus,
    getGridItemClasses,
    getGridItemStyle,
    shouldBeMinimized,
  };
}

export default useLayoutComposable;
